import { GetStaticProps, GetStaticPaths } from 'next';
import type { NextPage } from 'next';
import logger from '3-foundation/Logging/logger';
import { getPageContent } from '3-foundation/PageContent/repository';
import { DynamicPage } from '1-project/Pages/DynamicPage';
import { Layout } from '1-project/Layout/Layout';
import { getDictionaryItems } from '3-foundation/Dictionary/repository';
import { getNavigation } from '3-foundation/Navigation/repository';
import { getSite, getSiteConfig, getSiteRedirects } from '3-foundation/Site/repository';
import Application from '3-foundation/Application/ApplicationEntity';
import { PageProps } from '3-foundation/Types/page/pageProps';
import { resolveRedirectChain } from '3-foundation/Infrastructure/resolveRedirectChain';
import { withSiteLogContext } from '3-foundation/Logging/withSiteLogContext';
import {
	DISALLOWED_CONTENT_TYPES,
	NEXT_PUBLIC_SEARCH_CLUDO_CLIENT_ID,
	NEXT_PUBLIC_SEARCH_CLUDO_ENGINE_DA,
	NEXT_PUBLIC_SEARCH_CLUDO_ENGINE_EN,
	NEXT_PUBLIC_SEARCH_CLUDO_ENGINE_KL,
	NEXT_PUBLIC_SEARCH_SITE_ID_OVERRIDE,
	NEXT_PUBLIC_SEARCH_SITE_ENV,
	NEXT_PUBLIC_SEARCH_SITE_ID,
} from '1-project/_constants';

const Page: NextPage<PageProps> = (pageProps: PageProps) => {
	// Render happens after getStaticProps has returned, so it is outside the
	// request's site log context and has to name the site itself.
	logger.info('Rendering with ISR (incremental static regeneration)', { siteHost: pageProps?.site?.host });

	const { content } = pageProps ?? {};
	const { page } = content ?? {};

	return (
		<Layout {...pageProps}>
			<DynamicPage {...page} />
		</Layout>
	);
};

export const getStaticPaths: GetStaticPaths = async () => {
	return {
		paths: [],
		// In the current setup fallback MUST be blocking
		// If fallback is set to true next will try to return the
		// page with an initially empty data-set, so that you can show
		// a loading page or do other operations until data is available
		// https://nextjs.org/docs/api-reference/data-fetching/get-static-paths#fallback-blocking
		fallback: 'blocking',
	};
};

export const getStaticProps: GetStaticProps<PageProps> = withSiteLogContext(async (context) => {
	const { params, locale, preview } = context;
	const { hostname: encodedHostName, slug } = params;
	const hostName = atob(encodedHostName as string);
	const slugArr = slug as string[];
	const prefetch = slugArr?.includes('_sites');
	let path = '';

	if (!prefetch) path = slug ? slugArr.join('/') : '';
	if (prefetch) path = slug ? slugArr.slice(3).join('/') : '';

	const profiler = logger.startTimer();
	try {
		const { id: siteId, defaultLocale, cultures } = await getSiteConfig({ host: hostName, preview });
		const redirects = await getSiteRedirects({ siteId });
		const currentPath = locale === 'default' ? `/${path}` : `/${locale}/${path}`;
		const profilerRedirect = logger.startTimer();

		const resolvedRedirect = resolveRedirectChain(currentPath, redirects);
		profilerRedirect.done({ message: 'resolvedRedirectChain', level: 'debug' });

		if (resolvedRedirect) {
			if (!resolvedRedirect.destination || !resolvedRedirect.permanent) {
				logger.warn(
					`Redirect item is missing required properties. \nCurrentPath: ${currentPath}. \nDestination: ${resolvedRedirect.destination}`,
				);
			}
			// Without revalidate Next.js caches the redirect forever, so changed or removed
			// redirects would never be picked up from the redirects cache
			return {
				redirect: {
					destination: resolvedRedirect.destination,
					permanent: resolvedRedirect.permanent,
				},
				revalidate: 60,
			};
		}

		if (locale === 'default') {
			return { redirect: { destination: `/${defaultLocale}/${path}`, permanent: false }, revalidate: 60 };
		}
		if (!cultures?.[locale == 'kl-gl' ? 'kl-GL' : locale]) {
			logger.warn(`Locale '${locale}' is not configured for site ${siteId}. Handling the locale as a path instead of locale`);
			return { redirect: { destination: `/${defaultLocale}/${locale}${path ? '/' + path : ''}`, permanent: false }, revalidate: 60 };
		}

		// Set Cludo env vars in the Application Entity so they are accessible globally
		Application.setEnvVar('NEXT_PUBLIC_SEARCH_CLUDO_ENGINE_DA', NEXT_PUBLIC_SEARCH_CLUDO_ENGINE_DA);
		Application.setEnvVar('NEXT_PUBLIC_SEARCH_CLUDO_ENGINE_EN', NEXT_PUBLIC_SEARCH_CLUDO_ENGINE_EN);
		Application.setEnvVar('NEXT_PUBLIC_SEARCH_CLUDO_ENGINE_KL', NEXT_PUBLIC_SEARCH_CLUDO_ENGINE_KL);
		Application.setEnvVar('NEXT_PUBLIC_SEARCH_CLUDO_CLIENT_ID', NEXT_PUBLIC_SEARCH_CLUDO_CLIENT_ID);
		Application.setEnvVar('NEXT_PUBLIC_SEARCH_SITE_ID_OVERRIDE', NEXT_PUBLIC_SEARCH_SITE_ID_OVERRIDE);
		Application.setEnvVar('NEXT_PUBLIC_SEARCH_SITE_ENV', NEXT_PUBLIC_SEARCH_SITE_ENV);
		Application.setEnvVar('NEXT_PUBLIC_SEARCH_SITE_ID', NEXT_PUBLIC_SEARCH_SITE_ID);

		const site = await getSite({ siteId, locale, preview, host: hostName });
		const navigation = await getNavigation({ siteId, locale, preview, root: site.root });
		const dictionary = await getDictionaryItems({ locale, preview });
		const content = await getPageContent({
			siteId,
			site,
			locale,
			path,
			preview,
		});

		// The Application Entity contains functions as well as data so
		// to clean it and make it serializable we stringify and parse it
		const serializedApplication = JSON.parse(JSON.stringify(Application));

		if (DISALLOWED_CONTENT_TYPES.includes(content.page.documentType)) {
			return {
				notFound: true,
				revalidate: 15,
			};
		}

		return {
			props: {
				content,
				navigation,
				dictionary,
				site,
				application: serializedApplication,
				preview: preview || null,
			},
			revalidate: 1,
		};
	} catch (error) {
		const getStatusCode = (err): number | undefined => {
			if (err?.statusCode) return err.statusCode;
			if (err?.cause) return getStatusCode(err.cause);
			return undefined;
		};

		const statusCode = getStatusCode(error);

		if (statusCode === 404) {
			return {
				notFound: true,
				revalidate: 15,
			};
		}
		if (statusCode === 301 || statusCode === 302 || statusCode === 307 || statusCode === 308) {
			return {
				redirect: {
					destination: error?.cause?.response?.headers?.location,
					permanent: statusCode === 301 || statusCode === 308,
				},
				revalidate: 60,
			};
		}
		logger.error(`[${statusCode ?? 'no status'}] getStaticProps() \nCannot get static props \n${error}`, { statusCode });
		throw error;
	} finally {
		profiler.done({ message: 'getStaticProps', level: 'debug' });
	}
});

export default Page;
